import { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { analyticsService } from '@/services/analyticsService'
import toast from 'react-hot-toast'

interface NewPhoto {
  id: string
  userId: string
  originalUrl: string
  enhancedUrl: string
  thumbnailUrl: string
  createdAt: string
  isPublic: boolean
  isShared: boolean
  description: string
  tags: string[]
  tripId: string | null
  albumId: string | null
  eventId: string | null
  user: {
    id: string
    fullName: string
    email: string
    photoURL: string
  }
}

interface ChartData {
  date: string
  fullDate: string
  count: number
}

export const NewPhotosChart = () => {
  const [photos, setPhotos] = useState<NewPhoto[]>([])
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('7d')
  const [summary, setSummary] = useState<any>(null)

  useEffect(() => {
    const fetchNewPhotos = async () => {
      try {
        setLoading(true)
        const data = await analyticsService.getNewPhotos(selectedPeriod)
        
        setPhotos(data.photos)
        setSummary(data.summary)

        // Create chart data from photos
        const photosByDate = new Map<string, number>()
        data.photos.forEach(photo => {
          const dateKey = new Date(photo.createdAt).toISOString().split('T')[0]
          photosByDate.set(dateKey, (photosByDate.get(dateKey) || 0) + 1)
        })

        // Generate chart data for all dates in range
        const chartDataArray: ChartData[] = []
        const startDate = new Date(data.summary.dateRange.start)
        const endDate = new Date(data.summary.dateRange.end)
        
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split('T')[0]
          chartDataArray.push({
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: dateKey,
            count: photosByDate.get(dateKey) || 0
          })
        }

        setChartData(chartDataArray)
      } catch (error: any) {
        console.error('Failed to fetch new photos:', error)
        const errorMessage = error.response?.data?.error || 'Failed to load new photos data'
        toast.error(errorMessage)
        setPhotos([])
        setChartData([])
        setSummary(null)
      } finally {
        setLoading(false)
      }
    }

    fetchNewPhotos()
  }, [selectedPeriod])

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">New Photos</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="7d">7d</option>
            <option value="30d">30d</option>
            <option value="90d">90d</option>
          </select>
        </div>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">New Photos</h3>
          {summary && (
            <div className="flex items-center mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {summary.totalPhotos}
              </span>
              <span className="ml-2 text-sm font-medium text-gray-500">
                avg {summary.averagePhotosPerDay}/day
              </span>
            </div>
          )}
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="7d">7d</option>
          <option value="30d">30d</option>
          <option value="90d">90d</option>
        </select>
      </div>

      {chartData.length > 0 ? (
        <div className="h-48 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Date', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip 
                formatter={(value: number) => [value.toLocaleString(), 'New Photos']}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0] && payload[0].payload.fullDate) {
                    const date = new Date(payload[0].payload.fullDate)
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    }
                  }
                  return label
                }}
                labelStyle={{ color: '#374151' }}
              />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke="#eab308" 
                fill="#eab308"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-gray-500 mb-6">
          No new photos data available
        </div>
      )}

      {/* Recent Photos List */}
      {photos.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Photos</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {photos.map((photo) => {
              // Debug logging
              console.log('Photo data:', {
                id: photo.id,
                tripId: photo.tripId,
                albumId: photo.albumId,
                eventId: photo.eventId,
                hasTripId: Boolean(photo.tripId),
                hasAlbumId: Boolean(photo.albumId),
                hasEventId: Boolean(photo.eventId)
              })
              
              // Determine photo type
              let photoType = 'general'
              let photoTypeColor = 'gray'
              
              if (photo.tripId) {
                photoType = 'trip'
                photoTypeColor = 'blue'
              } else if (photo.albumId) {
                photoType = 'album'
                photoTypeColor = 'purple'
              } else if (photo.eventId) {
                photoType = 'event'
                photoTypeColor = 'green'
              }
              
              return (
                <div key={photo.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-lg bg-yellow-300 flex items-center justify-center">
                        <span className="text-sm font-medium text-yellow-600">
                          📸
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {photo.user.fullName}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs px-2 py-1 rounded ${
                          photoTypeColor === 'blue' ? 'bg-blue-100 text-blue-800' :
                          photoTypeColor === 'purple' ? 'bg-purple-100 text-purple-800' :
                          photoTypeColor === 'green' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {photoType === 'trip' ? 'Trip Photo' :
                           photoType === 'album' ? 'Album Photo' :
                           photoType === 'event' ? 'Event Photo' :
                           'General Photo'}
                        </span>
                        {/* Temporary debug info */}
                        <span className="text-xs text-gray-400">
                          (t:{photo.tripId || 'null'}, a:{photo.albumId || 'null'}, e:{photo.eventId || 'null'})
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-gray-500">
                      {new Date(photo.createdAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
} 